/**
 * Traffic Quality API service — per-campaign click-fraud analytics.
 */

import api from './api';
import type {
  TrafficQualityResponse,
  TrafficQualityPeriod,
  KeywordQuality,
  SourceQuality,
  FraudIp,
  FraudAlert,
  AccountTrafficQuality,
} from '../types/trafficQuality';

const params = (period: TrafficQualityPeriod) => ({ params: { period } });

export const trafficQualityService = {
  async getSummary(campaignId: number, period: TrafficQualityPeriod): Promise<TrafficQualityResponse> {
    const res = await api.get<TrafficQualityResponse>(
      `/api/campaigns/${campaignId}/traffic-quality`,
      params(period)
    );
    return res.data;
  },

  async getByKeyword(campaignId: number, period: TrafficQualityPeriod): Promise<KeywordQuality[]> {
    const res = await api.get<{ keywords: KeywordQuality[] }>(
      `/api/campaigns/${campaignId}/traffic-quality/by-keyword`,
      params(period)
    );
    return res.data.keywords;
  },

  async getBySource(campaignId: number, period: TrafficQualityPeriod): Promise<SourceQuality[]> {
    const res = await api.get<{ sources: SourceQuality[] }>(
      `/api/campaigns/${campaignId}/traffic-quality/by-source`,
      params(period)
    );
    return res.data.sources;
  },

  async getTopSources(campaignId: number, period: TrafficQualityPeriod): Promise<FraudIp[]> {
    const res = await api.get<{ ips: FraudIp[] }>(
      `/api/campaigns/${campaignId}/traffic-quality/top-sources`,
      params(period)
    );
    return res.data.ips;
  },

  async getAccountSummary(period: TrafficQualityPeriod): Promise<AccountTrafficQuality> {
    const res = await api.get<AccountTrafficQuality>(
      `/api/traffic-quality/account-summary`,
      params(period)
    );
    return res.data;
  },

  async getAlerts(campaignId: number): Promise<FraudAlert[]> {
    const res = await api.get<{ alerts: FraudAlert[] }>(
      `/api/campaigns/${campaignId}/fraud-alerts`
    );
    return res.data.alerts;
  },

  async blockAlert(campaignId: number, alertId: number): Promise<FraudAlert> {
    const res = await api.post<{ alert: FraudAlert }>(
      `/api/campaigns/${campaignId}/fraud-alerts/${alertId}/block`
    );
    return res.data.alert;
  },

  async dismissAlert(campaignId: number, alertId: number): Promise<FraudAlert> {
    const res = await api.post<{ alert: FraudAlert }>(
      `/api/campaigns/${campaignId}/fraud-alerts/${alertId}/dismiss`
    );
    return res.data.alert;
  },

  async exportGoogleAdsIps(campaignId: number, period: TrafficQualityPeriod): Promise<void> {
    const res = await api.get(
      `/api/campaigns/${campaignId}/fraud-alerts/export/google-ads-ips`,
      { params: { period }, responseType: 'blob' }
    );
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `ip-exclusions-campaign-${campaignId}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

export default trafficQualityService;
